import React, { useEffect, useRef, useState } from "react";
import { Flame, Cog, Wind, Sprout, ArrowRight } from "lucide-react";
import { ADVANTAGES } from "../mock";
import { scrollTo } from "./Navbar";
import { MobileCarbonReader } from "./MobileCarbonReader";

const ICONS = { Flame, Cog, Wind, Sprout };
const H = "8.125rem";
const H2 = "16.25rem";
const T = "carbon-motion transition-all duration-700 ease-out";

const Box = ({ children, variant = "solid", className = "" }) => {
  const cls = {
    solid: "border border-[#9FCDA1]/70 bg-[#1F1F1F] text-white/90",
    dashed: "border border-dashed border-white/45 text-white/65",
    filled: "bg-[#186449] text-white",
  }[variant];
  return (
    <div className={`w-[68px] h-[78px] min-[360px]:w-[78px] sm:w-28 sm:h-28 flex items-center justify-center text-center text-xs sm:text-sm leading-tight px-1 min-[360px]:px-2 ${cls} ${className}`}>
      {children}
    </div>
  );
};

const Op = ({ children, className = "" }) => (
  <span className={`text-3xl font-light text-green inline-flex items-center justify-center w-5 min-[360px]:w-6 sm:w-8 ${className}`} style={{ height: H }}>{children}</span>
);

export const CarbonPanel = ({ removed }) => (
  <div className="grid lg:grid-cols-5 gap-6 lg:gap-12 items-center" data-testid="carbon-panel">
    <div className="lg:col-span-2 relative grid lg:block min-h-[230px] sm:min-h-[240px]">
      <div className={`relative col-start-1 row-start-1 lg:absolute lg:inset-0 ${T} ${removed ? "opacity-0 -translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"}`} data-testid="carbon-title-avoided">
        <p className="label">Compensación tradicional</p>
        <h2 className="h-section mt-5">Evitar emisiones en otro lugar no cambia lo que ya está en la atmósfera.</h2>
        <p className="text-muted mt-5">1 t emitida + 1 t evitada = 1 t sigue en la atmósfera.</p>
      </div>
      <div className={`relative col-start-1 row-start-1 lg:absolute lg:inset-0 ${T} ${removed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`} data-testid="carbon-title-removed">
        <p className="label">Eliminación de carbono · Arlan</p>
        <h2 className="h-section mt-5">Retiramos CO₂ de la atmósfera. Esa es la diferencia.</h2>
        <p className="text-muted mt-5">1 t emitida + 1 t eliminada = 0. Net-zero real.</p>
      </div>
    </div>

    <div className="lg:col-span-3 flex justify-center lg:justify-end" data-testid="carbon-diagram">
      <div className="relative flex items-start gap-1 min-[360px]:gap-1.5 sm:gap-5">
        <span className="absolute left-0 right-0 h-px bg-white/40" style={{ top: H }} aria-hidden="true" />
        <div className="flex items-end" style={{ height: H }}><Box>1 t de CO₂<br />emitido</Box></div>
        <Op className={`${T} ${removed ? "translate-y-[8.125rem]" : "translate-y-0"}`}>+</Op>
        <div className="relative w-[68px] min-[360px]:w-[78px] sm:w-28" style={{ height: H2 }}>
          <div className={`absolute bottom-1/2 ${T} ${removed ? "opacity-0 -translate-y-6" : "opacity-100 translate-y-0"}`} data-testid="box-avoided">
            <Box variant="dashed">1 t de CO₂<br />evitado en<br />otro lugar</Box>
          </div>
          <div className={`absolute top-1/2 ${T} ${removed ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6"}`} data-testid="box-removed">
            <Box variant="filled">1 t de CO₂<br />eliminado</Box>
          </div>
        </div>
        <Op>=</Op>
        <div className="relative w-[68px] min-[360px]:w-[78px] sm:w-28" style={{ height: H }}>
          <div className={`absolute inset-0 flex items-end ${T} ${removed ? "opacity-0 scale-90" : "opacity-100 scale-100"}`} data-testid="result-avoided">
            <Box>1 t de CO₂<br />emitido</Box>
          </div>
          <div className={`absolute inset-0 flex flex-col items-center justify-end pb-1 ${T} ${removed ? "opacity-100 scale-100" : "opacity-0 scale-90"}`} data-testid="result-removed">
            <span className="text-6xl sm:text-7xl font-bold text-green leading-none">0</span>
            <span className="text-xs text-white/70 mt-2 whitespace-nowrap">net-zero CO₂</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const AdvantagesPanel = () => (
  <div id="ventajas" data-testid="advantages-panel">
    <p className="label">Nuestras ventajas</p>
    <h2 className="h-section mt-4">Referentes en energía que elimina CO₂ para la industria</h2>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {ADVANTAGES.map((a, i) => {
        const Icon = ICONS[a.icon];
        return (
          <button key={a.link} onClick={() => scrollTo(a.target)} className="group text-left border-t border-line pt-6 flex flex-col gap-4 hover:border-[#9FCDA1] transition-colors" data-testid={`advantage-${i}`}>
            <Icon className="w-10 h-10 text-green" strokeWidth={1} />
            <p className="text-white text-base leading-snug flex-1">{a.text}</p>
            <span className="link-arrow text-sm">{a.link} <ArrowRight className="w-4 h-4" /></span>
          </button>
        );
      })}
    </div>
  </div>
);

const useDesktop = () => {
  const [d, setD] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const f = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return d;
};

/* Desktop: sticky 1 pantalla. progreso <0.4 compensación, ≥0.4 eliminación; después, ventajas debajo. */
const CarbonStory = () => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  const desktop = useDesktop();

  useEffect(() => {
    if (!desktop) return;
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setProgress(Math.min(1, Math.max(0, -r.top / (1.2 * window.innerHeight))));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [desktop]);

  if (!desktop) {
    return (
      <MobileCarbonReader renderPanel={(removed) => <CarbonPanel removed={removed} />}>
        <AdvantagesPanel />
      </MobileCarbonReader>
    );
  }

  const removed = progress >= 0.4;

  return (
    <div className="bg-2">
      <section ref={ref} className="relative" style={{ height: "220vh" }} data-testid="carbon-section" data-state={removed ? "removed" : "avoided"}>
        <div className="sticky top-0 h-screen overflow-hidden">
          <div className="h-full px-12 pt-[96px] pb-10 flex items-center">
            <div className="max-w-6xl mx-auto w-full"><CarbonPanel removed={removed} /></div>
          </div>
          <div className="absolute bottom-8 left-12 flex items-center gap-3 text-xs text-muted" data-testid="carbon-progress">
            <span className={`h-px transition-all duration-300 ${removed ? "w-6 bg-white/30" : "w-14 bg-white"}`} />
            <span className={`h-px transition-all duration-300 ${removed ? "w-14 bg-white" : "w-6 bg-white/30"}`} />
            <span className="ml-2">{removed ? "Eliminación" : "Compensación"}</span>
          </div>
        </div>
      </section>
      <section className="px-12 pt-4 pb-28" data-testid="advantages-section">
        <div className="max-w-6xl mx-auto w-full"><AdvantagesPanel /></div>
      </section>
    </div>
  );
};

export default CarbonStory;
